import { FiX, FiCalendar, FiBook, FiStar, FiClock, FiMapPin } from 'react-icons/fi';
import { formatDate, formatTime, parseDateFromBackend } from '@/utils/dateUtils';
import { expandirReferencia } from '@/utils/bibliaHelper';

/**
 * Popup para mostrar detalles de un item (Cita, Lectura o Evento)
 */
export default function ItemPopup({ item, type, onClose }) {
  if (!item) return null;

  const getIcon = () => {
    switch (type) {
      case 'cita': return <FiCalendar className="w-6 h-6" />;
      case 'lectura': return <FiBook className="w-6 h-6" />;
      case 'evento': return <FiStar className="w-6 h-6" />;
      default: return <FiCalendar className="w-6 h-6" />;
    }
  };

  const getTypeLabel = () => {
    switch (type) {
      case 'cita': return 'Cita Ordenada';
      case 'lectura': return 'Lectura';
      case 'evento': return 'Evento';
      default: return 'Item';
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="p-4 border-b flex items-center justify-between"
          style={{ backgroundColor: `${item.color}20` }}
        >
          <div className="flex items-center gap-3">
            <div 
              className="p-2 rounded-lg"
              style={{ backgroundColor: item.color, color: 'white' }}
            >
              {getIcon()}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">
                {item.nombre || item.tipo}
              </h3>
              <p className="text-sm text-gray-600">{getTypeLabel()}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Descripción */}
          {item.descripcion && (
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Descripción</p>
              <p className="text-gray-800">{item.descripcion}</p>
            </div>
          )}

          {/* Referencia (para Lecturas) */}
          {type === 'lectura' && item.referencia && (
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Referencia</p>
              <p className="text-gray-800 font-medium">📖 {expandirReferencia(item.referencia)}</p>
            </div>
          )}

          {/* Cita bíblica (para Citas Ordenadas) */}
          {type === 'cita' && item.cita && (
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Cita Bíblica</p>
              <p className="text-gray-800 italic">"{item.cita}"</p>
            </div>
          )}

          {/* Tipo y Subtipo */}
          {(item.tipo || item.subtipo) && (
            <div className="flex gap-4">
              {item.tipo && (
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-600 mb-1">Tipo</p>
                  <span className="inline-block px-3 py-1 rounded-full text-sm" style={{ backgroundColor: `${item.color}30`, color: item.color }}>
                    {item.tipo}
                  </span>
                </div>
              )}
              {item.subtipo && (
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-600 mb-1">Subtipo</p>
                  <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                    {item.subtipo}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Categoría (para Eventos) */}
          {type === 'evento' && item.categoria && (
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Categoría</p>
              <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                {item.categoria}
              </span>
            </div>
          )}

          {/* Fechas */}
          {(item.fecha || item.fechaInicio) && (
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-2">
                <FiClock className="inline w-4 h-4 mr-1" />
                Fecha y Hora
              </p>
              {item.fecha && (
                <p className="text-gray-800">
                  {formatDate(parseDateFromBackend(item.fecha), 'EEEE, d \'de\' MMMM \'de\' yyyy')}
                </p>
              )}
              {item.fechaInicio && (
                <div className="space-y-1">
                  <p className="text-gray-800">
                    <span className="font-semibold">Inicio:</span> {formatDate(parseDateFromBackend(item.fechaInicio), 'dd/MM/yyyy')} a las {formatTime(parseDateFromBackend(item.fechaInicio))}
                  </p>
                  {item.fechaFin && (
                    <p className="text-gray-800">
                      <span className="font-semibold">Fin:</span> {formatDate(parseDateFromBackend(item.fechaFin), 'dd/MM/yyyy')} a las {formatTime(parseDateFromBackend(item.fechaFin))}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Duración */}
          {item.duracion && (
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Duración</p>
              <p className="text-gray-800">
                {item.duracion >= 1440 
                  ? `${Math.floor(item.duracion / 1440)} día${Math.floor(item.duracion / 1440) > 1 ? 's' : ''}`
                  : item.duracion >= 60
                  ? `${Math.floor(item.duracion / 60)} hora${Math.floor(item.duracion / 60) > 1 ? 's' : ''} ${item.duracion % 60 ? `${item.duracion % 60} min` : ''}`
                  : `${item.duracion} minutos`
                }
              </p>
            </div>
          )}

          {/* Periodicidad (para Eventos) */}
          {type === 'evento' && item.periodicidad && item.periodicidad !== 'unica' && (
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Periodicidad</p>
              <p className="text-gray-800 capitalize">
                🔄 {item.periodicidad}
                {item.diaSeamana !== undefined && ` - ${['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'][item.diaSemana]}`}
              </p>
            </div>
          )}

          {/* Palabras clave (para Eventos) */}
          {type === 'evento' && item.palabrasClave && Array.isArray(item.palabrasClave) && item.palabrasClave.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-2">Palabras Clave</p>
              <div className="flex flex-wrap gap-2">
                {item.palabrasClave.map((palabra, idx) => (
                  <span 
                    key={idx}
                    className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs"
                  >
                    #{palabra}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Información adicional (Parasha para lecturas) */}
          {type === 'lectura' && item.parasha && (
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Parashá</p>
              <p className="text-gray-800">{item.parasha}</p>
            </div>
          )}
        </div>

        {/* Footer con acción */}
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

